import { formatSeconds, goalDataTypeEnums } from 'utils';

const PERCENTAGE_DATA_TYPES = new Set([
    goalDataTypeEnums.PERCENT,
    goalDataTypeEnums.WHOLE_INTERVAL,
    goalDataTypeEnums.PARTIAL_INTERVAL,
    goalDataTypeEnums.MOMENTARY_TIME_SAMPLING,
]);

const roundNumber = (value, digits = 2) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return null;

    return Number(value).toFixed(digits);
};

/** A point with a value to show — not a gap (`null`, or ECharts' `'-'`). */
export const hasChartValue = (value) => value !== null && value !== undefined && !Number.isNaN(Number(value));

export const isPercentageDataType = (dataType) => PERCENTAGE_DATA_TYPES.has(dataType);

export const getChartValueLabelByDataType = (dataType) => {
    switch (dataType) {
        case goalDataTypeEnums.PERCENT:
            return 'Percent Correct';
        case goalDataTypeEnums.FREQUENCY:
            return 'Frequency';
        case goalDataTypeEnums.RATING:
            return 'Rating';
        case goalDataTypeEnums.DURATION:
            return 'Duration';
        case goalDataTypeEnums.LATENCY:
            return 'Latency';
        case goalDataTypeEnums.SCORE:
            return 'Score';
        case goalDataTypeEnums.WHOLE_INTERVAL:
        case goalDataTypeEnums.PARTIAL_INTERVAL:
        case goalDataTypeEnums.MOMENTARY_TIME_SAMPLING:
            return 'Average';
        case goalDataTypeEnums.RATE:
            return 'Rate';
        default:
            return 'Value';
    }
};

export const formatChartValueByDataType = (value, dataType) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return 'No session data';

    const numericValue = Number(value);

    switch (dataType) {
        case goalDataTypeEnums.RATE:
            return `${roundNumber(numericValue, 2)}`;
        case goalDataTypeEnums.PERCENT:
        case goalDataTypeEnums.WHOLE_INTERVAL:
        case goalDataTypeEnums.PARTIAL_INTERVAL:
        case goalDataTypeEnums.MOMENTARY_TIME_SAMPLING:
            return `${roundNumber(numericValue, 0)}%`;
        case goalDataTypeEnums.LATENCY:
        case goalDataTypeEnums.DURATION:
        case goalDataTypeEnums.IRT:
            return formatSeconds(numericValue);
        case goalDataTypeEnums.FREQUENCY:
            return `${roundNumber(numericValue, 0)}`;
        case goalDataTypeEnums.RATING:
            return `${roundNumber(numericValue, 1)}`;
        case goalDataTypeEnums.SCORE:
            return `${roundNumber(numericValue, 2)}`;
        default:
            return `${roundNumber(numericValue, 2)}`;
    }
};

/** Longest a target name may run in a multi-target chart legend before it is cut with an ellipsis. */
const TARGET_LEGEND_LABEL_WIDTH = 150;

/**
 * Legend for a chart with one line per target. Target names can be long and a goal can have dozens,
 * so the legend stays on one row: each name is cut to TARGET_LEGEND_LABEL_WIDTH (the full name shows
 * on hover) and the rest of the targets page with arrows instead of wrapping over the x-axis.
 * Pair it with a grid `bottom` of TARGET_LEGEND_GRID_BOTTOM.
 */
export const buildTargetLegendOption = ({ names, fontFamily }) => ({
    type: 'scroll',
    bottom: 0,
    left: 'center',
    width: '90%',
    itemWidth: 10,
    itemHeight: 10,
    itemGap: 16,
    data: names,
    tooltip: { show: true },
    textStyle: {
        fontFamily,
        fontSize: 11,
        color: '#667085',
        width: TARGET_LEGEND_LABEL_WIDTH,
        overflow: 'truncate',
        ellipsis: '…',
    },
    pageIconColor: '#0C66E4',
    pageIconInactiveColor: '#D0D5DD',
    pageIconSize: 10,
    pageTextStyle: { fontFamily, fontSize: 11, color: '#667085' },
});

/** Room under the plot for the x-axis labels, the "Time" axis name and the one-row target legend. */
export const TARGET_LEGEND_GRID_BOTTOM = 64;
